/**
 * SF-4373 — "CSAT Custom Domain" tab on the company detail page.
 *
 * SF-4656: the on/off switch for the chargeable add-on moved to the Feature
 * Flags tab, and the page only shows this tab while the add-on is ON. So this
 * tab is the setup screen only: the company default for links and emails,
 * and the domain list (CsatCustomDomainSection). It still refuses to render
 * when the add-on is off, in case it is ever mounted directly.
 *
 * One save model: the default-host radio saves its single field straight
 * away through the page's shared saver (useSaveCompanyField), so it follows
 * the page's one-save-at-a-time rule: it is locked while any other save on
 * the company runs, and its own save locks the rest (SF-4656, Bugbot). The
 * company comes from the detail page's own query (one fetch, one cache
 * entry). A radio save never unmounts the domain section (that would drop a
 * selected row, an open sender draft and the test-send fields; Bugbot), the
 * radio just locks itself while it saves.
 *
 * Permissions: hidden without admin:companies:domain:read. Changing the
 * default needs BOTH admin:companies:write (the PATCH route) and
 * admin:companies:domain:write (the field), matching sf-api's checks; either
 * missing renders the radio disabled with a read-only note. Server enforces
 * all of this too; this is defence in depth.
 */
import { usePermissions } from '@refinedev/core';
import { Radio, Space, Spin, Typography } from 'antd';
import { hasPermission } from '../../../../utils/permissions';
import type { Company } from '../../list';
import type { PendingSave } from '../useSaveCompanyField';
import {
  CSAT_DOMAIN_READ_PERMISSION,
  CSAT_DOMAIN_WRITE_PERMISSION,
  type CsatDefaultLinkHost,
} from '../../api/csatDomainClient';
import { CsatCustomDomainSection } from './CsatCustomDomainSection';

const { Text } = Typography;

export interface CsatDomainTabProps {
  /** The company as loaded by the detail page (shared query, no second fetch). */
  company: Company;
  /** Whether the caller may edit companies at all (Refine `companies:edit`). */
  canEditCompany: boolean;
  /**
   * SF-4668: false when this company is not on the staff member's company list. Kept apart from
   * `canEditCompany` so the read-only note gives the right reason. Defaults to true.
   */
  companyOnList?: boolean;
  /** The page's single-field saver (one save at a time across the page). */
  saver: {
    save: <K extends keyof Company>(field: K, value: Company[K], successText: string) => Promise<boolean>;
    pending: PendingSave | null;
    busy: boolean;
  };
}

export const CsatDomainTab: React.FC<CsatDomainTabProps> = ({
  company,
  canEditCompany,
  companyOnList = true,
  saver,
}) => {
  const { data: permissions } = usePermissions<string[]>();
  const canRead = hasPermission(permissions ?? [], CSAT_DOMAIN_READ_PERMISSION);
  const canWriteDomain = hasPermission(permissions ?? [], CSAT_DOMAIN_WRITE_PERMISSION);
  const canWrite = canWriteDomain && canEditCompany && companyOnList;

  const companyId = String(company._id || company.id);
  const savedEnabled = !!company.csat_custom_domain_enabled;
  // Tier 1 of the two-tier link model (SF-4350): with the add-on on, staff
  // choose whether unpinned surveys/emails use the DEFAULT DOMAIN or the
  // standard ServeFirst address. Missing on older companies = "custom"
  // (unchanged behaviour).
  const savedDefaultHost: CsatDefaultLinkHost =
    company.csat_default_link_host === 'platform' ? 'platform' : 'custom';

  // Non-null while the radio's own save runs: the value it is heading to.
  const pendingHost =
    saver.pending?.field === 'csat_default_link_host'
      ? (saver.pending.value as CsatDefaultLinkHost)
      : null;

  if (!canRead || !savedEnabled) return null;

  const setDefaultHost = (next: CsatDefaultLinkHost) => {
    if (next === savedDefaultHost) return;
    // A failed save shows the server's reason as a toast and the radio goes
    // back to the saved value (the shared saver's error handling).
    void saver.save(
      'csat_default_link_host',
      next,
      next === 'platform'
        ? 'Company default set to the standard ServeFirst address'
        : 'Company default set to the default custom domain',
    );
  };

  const hostBusy = pendingHost !== null;
  const readOnlyNote = !companyOnList
    ? 'This company is not on your list of companies, so you can only view it.'
    : !canWriteDomain
      ? 'You have read-only access to custom-domain setup (admin:companies:domain:write required to change it).'
      : !canEditCompany
        ? 'Changing this also needs company edit rights (admin:companies:write).'
        : null;

  return (
    <div data-testid="csat-domain-tab">
      <div style={{ marginBottom: 16 }} data-testid="csat-default-link-host">
        <Text strong>Company default for links and emails</Text>
        {hostBusy && (
          <Spin
            size="small"
            style={{ marginLeft: 8 }}
            data-testid="csat-default-link-host-saving"
          />
        )}
        <br />
        <Radio.Group
          value={pendingHost ?? savedDefaultHost}
          onChange={(e) => setDefaultHost(e.target.value as CsatDefaultLinkHost)}
          disabled={!canWrite || saver.busy}
          style={{ marginTop: 4 }}
        >
          <Space direction="vertical" size={2}>
            <Radio value="custom" data-testid="csat-default-link-host-custom">
              The default custom domain (marked in the list below)
            </Radio>
            <Radio value="platform" data-testid="csat-default-link-host-platform">
              The standard ServeFirst address
            </Radio>
          </Space>
        </Radio.Group>
        <br />
        <Text type="secondary">
          {readOnlyNote ??
            'Surveys inherit this unless a survey overrides it in its own settings. Saved immediately.'}
        </Text>
      </div>
      <CsatCustomDomainSection
        companyId={companyId}
        enabled={savedEnabled}
        savedEnabled={savedEnabled}
      />
    </div>
  );
};
